<template>
  <div>
    <BContainer>
      <b-table
        :data="data"
        ref="table"
        :striped="true"
        :mobile-cards="true"
        detailed
        hoverable
        :show-detail-icon="showDetailIcon"
        :has-detailed-visible="row => row.users.length > 0"
        :default-sort="['createdAt', 'desc']"
      >
        <template slot-scope="props">
          <b-table-column
            field="firstname"
            :label="$t('validation.attributes.firstName')"
            sortable
            :custom-sort="sortByText(row => row.firstname)"
          >
            <template v-if="props.row.users.length < 1">{{ props.row.firstname }}</template>
            <template v-else>
              <a @click="toggle(props.row)">{{ props.row.firstname }}</a>
            </template>
          </b-table-column>
          <b-table-column
            field="lastname"
            :label="$t('validation.attributes.lastName')"
            sortable
            :custom-sort="sortByText(row => row.lastname)"
          >
            <template>{{ props.row.lastname }}</template>
          </b-table-column>
          <b-table-column
            field="company.name"
            :label="$t('validation.attributes.company')"
            sortable
            :custom-sort="sortByText(row => row.company && row.company.name)"
          >
            <template>{{ props.row.company ? props.row.company.name : '' }}</template>
          </b-table-column>
          <b-table-column
            field="package"
            :label="$t('title.package')"
            sortable
            :custom-sort="sortByText(row => $t(`title.${packageOf(row)}`))"
          >
            <span :class="['tag', packageTags[packageOf(props.row)]]">
              {{ $t(`title.${packageOf(props.row)}`) }}
            </span>
          </b-table-column>
          <b-table-column
            field="packageStatus"
            :label="$t('title.packageStatus')"
            sortable
            :custom-sort="sortByText(row => packageStatusLabel(row))"
          >
            <span
              v-if="packageStatusOf(props.row)"
              :class="['tag', packageStatusTags[packageStatusOf(props.row)]]"
            >
              {{ packageStatusLabel(props.row) }}
            </span>
          </b-table-column>
          <b-table-column
            field="isActive"
            :label="$t('validation.attributes.status')"
            sortable
            :custom-sort="sortByText(row => verificationLabel(row))"
          >
            <template v-if="props.row.isActive === true">
              <span class="tag is-white">{{ $t('title.verified') }}</span>
            </template>
            <template v-else-if="props.row.isActive === null">
              <span class="tag is-white">{{ $t('title.notVerified') }}</span>
            </template>
            <template v-else-if="props.row.isActive === false">
              <span class="tag is-white">{{ $t('title.passive') }}</span>
            </template>
          </b-table-column>
          <b-table-column field="createdAt" :label="$t('title.createdAt')" sortable>
            <template>{{ props.row.createdAt | datetime }}</template>
          </b-table-column>
          <b-table-column field="updatedAt" :label="$t('title.updatedAt')" sortable>
            <template>{{ props.row.updatedAt | datetime }}</template>
          </b-table-column>
          <b-table-column width="120" centered>
            <div class="buttons">
              <b-button icon-right="edit" @click="openEditModal(props.row.id)" />
            </div>
          </b-table-column>
        </template>

        <template slot="detail" slot-scope="props">
          <div class="table-container">
            <table class="table is-narrow">
              <thead>
                <th>{{ $t('validation.attributes.firstName') }}</th>
                <th>{{ $t('validation.attributes.lastName') }}</th>
                <th>{{ $t('validation.attributes.status') }}</th>
                <th>{{ $t('title.createdAt') }}</th>
                <th>{{ $t('title.updatedAt') }}</th>
                <th></th>
              </thead>
              <tbody>
                <tr v-for="item in props.row.users" :key="item.id">
                  <td>{{ item.firstname }}</td>
                  <td>{{ item.lastname }}</td>
                  <td>
                    <span class="tag is-success" v-if="item.isActive === true">{{
                      $t('title.verified')
                    }}</span>
                    <span class="tag is-danger" v-else-if="item.isActive === null">{{
                      $t('title.notVerified')
                    }}</span>
                    <span class="tag is-black" v-else-if="item.isActive === false">{{
                      $t('title.passive')
                    }}</span>
                  </td>
                  <td>{{ item.createdAt | datetime }}</td>
                  <td>{{ item.updatedAt | datetime }}</td>
                  <td>
                    <div class="buttons">
                      <b-button icon-right="edit" @click="openEditModal(item.id)" />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </b-table>
    </BContainer>
  </div>
</template>

<script>
import ApiService from '../../../_api/api.service'
import UserEdit from './UserEdit.vue'

const collator = new Intl.Collator('tr', { sensitivity: 'base' })

export default {
  data () {
    return {
      data: [],
      showDetailIcon: true,
      packageTags: {
        admin: 'is-dark',
        free: 'is-light',
        forCommercial: 'is-info',
        corporateLicense: 'is-success'
      },
      packageStatusTags: { expired: 'is-danger', active: 'is-success' }
    }
  },
  mounted () {
    this.getData()
  },
  methods: {
    getData () {
      ApiService.get('/user/all').then(data => {
        const mainUsers = data.filter(x => x.parentCompanyId === null)
        this.data = mainUsers.map(m => {
          const row = m
          row.users = []
          if (row.companyId != null) {
            row.users = data
              .filter(u => u.parentCompanyId === m.companyId)
              .sort((a, b) =>
                collator.compare(a.firstname || '', b.firstname || '')
              )
          }
          return row
        })
      })
    },
    // Buefy compares upper-cased strings by code point, which puts Ç, İ, Ö,
    // Ş, Ü after Z; empty values stay last in both directions, as in Buefy.
    sortByText (getValue) {
      return (a, b, isAsc) => {
        const valueA = getValue(a) || ''
        const valueB = getValue(b) || ''
        if (!valueA || !valueB) return !valueA - !valueB
        const order = collator.compare(valueA, valueB)
        return isAsc ? order : -order
      }
    },
    packageOf (row) {
      if (row.roles.includes('admin')) return 'admin'
      if (row.roles.includes('limited')) return 'free'
      return row.companyId !== null ? 'forCommercial' : 'corporateLicense'
    },
    packageStatusOf (row) {
      const { company } = row
      const isAdmin = row.roles.includes('admin')
      const isLimited = row.roles.includes('limited')
      if (
        (!isAdmin &&
          !isLimited &&
          row.companyId === null &&
          row.membershipId === null) ||
        (company &&
          company.membershipEndDate != null &&
          new Date(company.membershipEndDate) < new Date())
      ) {
        return 'expired'
      }
      if (
        (!isLimited && row.companyId === null && row.membershipId !== null) ||
        (company &&
          (company.membershipEndDate === null ||
            new Date(company.membershipEndDate) >= new Date()))
      ) {
        return 'active'
      }
      return null
    },
    packageStatusLabel (row) {
      const status = this.packageStatusOf(row)
      return status ? this.$t(`title.${status}`) : ''
    },
    verificationLabel (row) {
      if (row.isActive === true) return this.$t('title.verified')
      if (row.isActive === null) return this.$t('title.notVerified')
      if (row.isActive === false) return this.$t('title.passive')
      return ''
    },
    toggle (row) {
      this.$refs.table.toggleDetails(row)
    },
    openEditModal (id) {
      this.$buefy.modal.open({
        parent: this,
        props: { id },
        component: UserEdit,
        events: {
          saved: () => {
            this.getData()
          }
        }
      })
    }
  }
}
</script>
